import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import type { ThemeMode } from '../hooks/useTheme';
import { Z_INDEX } from '../styles/zIndex';
import { selectOnFocus } from '../util/focusField';
import { useAppSocket } from './AppSocketContext';

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  isDark: boolean;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const PRIMARY_COLOR_KEYS = [
  'UI_PRIMARY_COLOR',
  'PRIMARY_COLOR',
  'BIZ_PRIMARY_COLOR',
  'APP_PRIMARY_COLOR',
  'THEME_COLOR',
  'ACCENT_COLOR',
  'UI_THEME_PRIMARY'
];

const normalizeColorValue = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;

  const candidate = /^([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/.test(raw) ? `#${raw}` : raw;
  return typeof window !== 'undefined' && window.CSS?.supports?.('color', candidate) ? candidate : null;
};

const resolvePrimaryColorFromSettings = (appSettings: any | null): string | null => {
  if (!appSettings || typeof appSettings !== 'object') return null;

  for (const key of PRIMARY_COLOR_KEYS) {
    const resolved = normalizeColorValue(appSettings?.[key]?.value);
    if (resolved) return resolved;
  }

  for (const [key, config] of Object.entries<any>(appSettings)) {
    const keyLooksLikePrimaryColor = /(PRIMARY|ACCENT|THEME).*COLOR|COLOR.*(PRIMARY|ACCENT|THEME)/i.test(key);
    if (!keyLooksLikePrimaryColor) continue;
    const resolved = normalizeColorValue(config?.value);
    if (resolved) return resolved;
  }

  return null;
};

export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  const { appSettings } = useAppSocket();
  const [theme, setTheme] = useState<ThemeMode>(() =>
    (localStorage.getItem('theme') as ThemeMode) || 'light'
  );

  const isDark = useMemo(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme === 'dark';
  }, [theme]);

  // Apply data-theme attribute and persist selection.
  // This runs after render; CSS-variable-based styles (nav, topbar, bottombar)
  // update from here while the MUI palette uses hardcoded values below so it is
  // always correct on the same render that isDark changes.
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.toggle('dark', isDark);
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme', theme);
  }, [theme, isDark]);

  const settingsPrimaryColor = useMemo(
    () => resolvePrimaryColorFromSettings(appSettings),
    [appSettings]
  );

  useEffect(() => {
    const root = window.document.documentElement;
    if (settingsPrimaryColor) {
      root.style.setProperty('--primary-color', settingsPrimaryColor);
      return;
    }
    root.style.removeProperty('--primary-color');
  }, [settingsPrimaryColor]);

  const muiTheme = useMemo(
    () => {
      const fallbackPrimaryColor = isDark ? '#ef5350' : '#8b1010';
      const primaryMain = settingsPrimaryColor || fallbackPrimaryColor;

      return createTheme({
        spacing: 8,
        shape: {
          borderRadius: 10,
        },
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: primaryMain,
          },
          secondary: {
            // Values match theme.css --secondary-color
            main: isDark ? '#ffd54f' : '#deb111',
          },
          background: {
            // Values match theme.css --bg-color / --bg-surface
            default: isDark ? '#11151b' : '#f7f8fb',
            paper: isDark ? '#1e1e1e' : '#ffffff',
          },
          text: {
            // Values match theme.css --text-primary / --text-secondary
            primary: isDark ? '#e0e0e0' : '#212121',
            secondary: isDark ? '#c7c7c7' : '#555555',
          },
          // Values match theme.css --border-color
          divider: isDark ? '#3c3c3c' : '#d8dde7',
          success: {
            // Values match theme.css --success-color
            main: isDark ? '#3ddc84' : '#198754',
          },
          warning: {
            // Values match theme.css --warning-color
            main: isDark ? '#f7c948' : '#f59e0b',
          },
          error: {
            // Values match theme.css --error-color
            main: isDark ? '#ff6b6b' : '#dc3545',
          },
          info: {
            // Values match theme.css --info-color
            main: isDark ? '#60a5fa' : '#2563eb',
          },
        },
        typography: {
          fontFamily: "'Segoe UI', 'Inter', 'Arial', sans-serif",
          h6: {
            fontWeight: 700,
            letterSpacing: 0.2,
          },
          subtitle1: {
            fontWeight: 600,
          },
          body2: {
            lineHeight: 1.45,
          },
          caption: {
            fontSize: '0.75rem',
            letterSpacing: 0.2,
          },
          button: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
        zIndex: {
          appBar: Z_INDEX.appBar,
          drawer: Z_INDEX.drawer,
          modal: Z_INDEX.dialog,
          snackbar: Z_INDEX.snackbar,
          tooltip: Z_INDEX.tooltip,
        },
        components: {
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                borderRadius: 8,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              size: 'small',
              variant: 'outlined',
              // Keyboard-first entry: numeric fields (qty, price, amount) select on focus so typing replaces.
              // An instance onFocus replaces this default.
              onFocus: (event) => {
                const input = event.target;
                if (input instanceof HTMLInputElement && (input.type === 'number' || input.dataset.selectOnFocus != null)) {
                  selectOnFocus(event);
                }
              },
            },
          },
          MuiAutocomplete: {
            defaultProps: {
              slotProps: {
                popupIndicator: { tabIndex: -1 },
                clearIndicator: { tabIndex: -1 },
              },
            },
          },
          MuiDialog: {
            defaultProps: {
              maxWidth: 'sm',
              fullWidth: true,
            },
            styleOverrides: {
              paper: {
                borderRadius: 12,
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                borderRadius: 10,
              },
            },
          },
        },
      });
    },
    [isDark, settingsPrimaryColor]
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
